/**
 * Operator session.
 *
 * Same custody rule as the customer app — the access token stays in memory and
 * only the refresh token is persisted — but under a **different storage key and
 * a different origin**, so signing into the panel never grants anything to the
 * customer bundle and vice versa.
 */

const REFRESH_KEY = 'admin_refresh_token'

export interface Tokens {
  accessToken: string
  refreshToken: string
}

let accessToken: string | null = null
const listeners = new Set<(authenticated: boolean) => void>()

function read(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch {
    return null
  }
}

let refreshToken: string | null = read()

function notify(): void {
  for (const listener of listeners) listener(refreshToken !== null)
}

export function getAccessToken(): string | null {
  return accessToken
}

export function getRefreshToken(): string | null {
  return refreshToken
}

export function setTokens(tokens: Tokens): void {
  accessToken = tokens.accessToken
  refreshToken = tokens.refreshToken
  try {
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken)
  } catch {
    // The tab still works without persistence.
  }
  notify()
}

export function clearTokens(): void {
  accessToken = null
  refreshToken = null
  try {
    localStorage.removeItem(REFRESH_KEY)
  } catch {
    // Nothing to clear.
  }
  notify()
}

export function isAuthenticated(): boolean {
  return refreshToken !== null
}

export function onSessionChange(listener: (authenticated: boolean) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
